'use client'

import {
  IAddressBook,
  IBasicInquiryLoadLite,
  IInquiryLoadDrops,
  TravelPointQuoteWithAddressId,
  useLoadsApi,
  useUpFetch,
} from '@repo/sdk'
import {
  AddressBookContextProvider,
  Button,
  DialogFooter,
  DropFieldsList,
  Form,
  Spinner,
} from '@repo/ui'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

type Props = {
  companyId?: number
  inquiry: IBasicInquiryLoadLite | IInquiryLoadDrops
  onConfirm: (inquiry: IBasicInquiryLoadLite | IInquiryLoadDrops) => void
  onSuccess?: () => Promise<void>
  userId?: number
}

export function AddressBookForm({ companyId, inquiry, onConfirm, onSuccess, userId }: Props) {
  const router = useRouter()
  const upFetch = useUpFetch()

  const { patchLoadAddress } = useLoadsApi()

  const form = useForm<{ drops: TravelPointQuoteWithAddressId[] }>({
    defaultValues: {
      drops: inquiry.load.drops,
    },
  })

  const { isPending, mutate } = useMutation({
    mutationFn: async ({ drops }: { drops: TravelPointQuoteWithAddressId[] }) => {
      if (!companyId) throw new Error('Please provide your company details')

      for (const drop of drops) {
        if (!drop.addressId) {
          const response = await upFetch<{ data: IAddressBook }>(
            `companies/${inquiry.load.company?.id ?? companyId}/address-book`,
            {
              body: {
                city: drop.place?.city ?? '',
                name: drop.place?.name ?? '',
                post_code: drop.place?.postcode ?? '',
                street: drop.place?.place_name ?? '',
                user_id: inquiry.load.user?.id ?? userId,
              },
              method: 'POST',
            },
          )

          if (!response) throw new Error('Failed to set address')
          drop.addressId = response.data.address.id
        }

        if (drop.addressId) {
          await patchLoadAddress({
            addressId: drop.addressId,
            dropId: drop.id ?? 0,
            loadId: inquiry.load_id,
          })
        }
      }

      onConfirm(inquiry)
    },
    onError: error => {
      if ('response' in error) {
        // @ts-ignore
        toast.error(error?.response?.data?.message ?? 'Failed to book quote', {
          duration: 10000,
          position: 'top-right',
        })
      } else if (error instanceof Error) {
        toast.error(error.message, {
          action: {
            label: 'Settings',
            onClick: () => {
              router.push('/settings/company')
            },
          },
          duration: 10000,
          position: 'top-right',
        })
      }
      console.warn(error)
    },
    onSuccess: async () => {
      toast.success('Quote booked successfully', {
        duration: 15000,
        position: 'top-right',
      })
      await onSuccess?.()
    },
  })

  const onSubmit = (input: { drops: TravelPointQuoteWithAddressId[] }) => {
    mutate(input)
  }

  return (
    <Form {...form}>
      <form className={'flex w-full flex-col gap-4'} onSubmit={form.handleSubmit(onSubmit)}>
        <AddressBookContextProvider
          companyId={inquiry.load.company?.id ?? companyId}
          userId={inquiry.load.user?.id ?? userId}
        >
          <DropFieldsList />
        </AddressBookContextProvider>
        <DialogFooter>
          <Button type={'button'} variant={'secondary'}>
            Cancel
          </Button>
          <Button disabled={isPending} type={'submit'}>
            Confirm {isPending && <Spinner />}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
